// Binary codec for SnapshotMessage and SnapshotDeltaMessage. Hand-rolled,
// no third-party dependency. Used only on the snapshot/delta hot path —
// control messages stay JSON for human-debuggability.
//
// Wire format:
//   [magic u8 = 0x54 'T'][version u8 = 0x01][messageType u8: 0=snapshot, 1=delta]
//   ...payload (see encode functions below)
//
// Schema evolution rule: any change that adds/removes/reorders a field, or
// changes its wire type, requires bumping VERSION. Adding a new optional
// field that rides on an existing presence bitmap is backward-compatible
// only if the bitmap has a free bit AND the decoder tolerates the absent
// field for older encoders. In practice: bump the version on any change
// and let the client refresh.
import { FACTION_IDS, UNIT_TYPE_INDEX, UNIT_TYPE_TO_INDEX, TOWER_TYPE_INDEX, TOWER_TYPE_TO_INDEX, } from "./index.js";
const MAGIC = 0x54; // 'T'
const VERSION = 0x02; // bumped 2026-05-13 for factionPicks field on PlayerSnapshot/Delta.
const MSG_SNAPSHOT = 0;
const MSG_SNAPSHOT_DELTA = 1;
const NULL_TICK = 0xFFFFFFFF; // sentinel for `nextFillTick`/`unlockTick === null`
const ENC = new TextEncoder();
const DEC = new TextDecoder();
// ---------------------------------------------------------------------------
// Writer / Reader primitives
// ---------------------------------------------------------------------------
class Writer {
    buf;
    view;
    cursor = 0;
    constructor(initialCap = 1024) {
        this.buf = new Uint8Array(initialCap);
        this.view = new DataView(this.buf.buffer);
    }
    ensure(extra) {
        const need = this.cursor + extra;
        if (need <= this.buf.byteLength)
            return;
        let cap = this.buf.byteLength;
        while (cap < need)
            cap *= 2;
        const next = new Uint8Array(cap);
        next.set(this.buf);
        this.buf = next;
        this.view = new DataView(next.buffer);
    }
    u8(v) {
        this.ensure(1);
        this.buf[this.cursor++] = v & 0xFF;
    }
    u16(v) {
        this.ensure(2);
        this.view.setUint16(this.cursor, v, true);
        this.cursor += 2;
    }
    u32(v) {
        this.ensure(4);
        this.view.setUint32(this.cursor, v >>> 0, true);
        this.cursor += 4;
    }
    f32(v) {
        this.ensure(4);
        this.view.setFloat32(this.cursor, v, true);
        this.cursor += 4;
    }
    varuint(v) {
        // LEB128 unsigned. Counts only — caller guarantees v >= 0 and < 2^28.
        while (v > 0x7F) {
            this.u8((v & 0x7F) | 0x80);
            v >>>= 7;
        }
        this.u8(v);
    }
    string(s) {
        // Length-prefixed u8. UUIDs, type tags, tech ids — all <= 64 bytes.
        const bytes = ENC.encode(s);
        if (bytes.length > 255)
            throw new Error(`string too long for u8 prefix: ${bytes.length}`);
        this.u8(bytes.length);
        this.ensure(bytes.length);
        this.buf.set(bytes, this.cursor);
        this.cursor += bytes.length;
    }
    bigString(s) {
        // Length-prefixed u16 — for displayName (sanitized to ~32 bytes but headroom).
        const bytes = ENC.encode(s);
        if (bytes.length > 0xFFFF)
            throw new Error(`bigString too long: ${bytes.length}`);
        this.u16(bytes.length);
        this.ensure(bytes.length);
        this.buf.set(bytes, this.cursor);
        this.cursor += bytes.length;
    }
    finish() {
        // Slice (not copy) — caller must consume before any further writes.
        return this.buf.subarray(0, this.cursor);
    }
}
class Reader {
    view;
    buf;
    cursor = 0;
    constructor(buf) {
        this.buf = buf;
        this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    }
    u8() { const v = this.view.getUint8(this.cursor); this.cursor += 1; return v; }
    u16() { const v = this.view.getUint16(this.cursor, true); this.cursor += 2; return v; }
    u32() { const v = this.view.getUint32(this.cursor, true); this.cursor += 4; return v; }
    f32() { const v = this.view.getFloat32(this.cursor, true); this.cursor += 4; return v; }
    varuint() {
        let v = 0;
        let shift = 0;
        while (true) {
            const b = this.u8();
            v |= (b & 0x7F) << shift;
            if ((b & 0x80) === 0)
                return v >>> 0;
            shift += 7;
            if (shift > 28)
                throw new Error("varuint overflow");
        }
    }
    string() {
        const len = this.u8();
        const start = this.cursor;
        this.cursor += len;
        return DEC.decode(this.buf.subarray(start, this.cursor));
    }
    bigString() {
        const len = this.u16();
        const start = this.cursor;
        this.cursor += len;
        return DEC.decode(this.buf.subarray(start, this.cursor));
    }
}
function writeNullableTick(w, v) {
    w.u32(v === null ? NULL_TICK : v);
}
function readNullableTick(r) {
    const v = r.u32();
    return v === NULL_TICK ? null : v;
}
function writeCell(w, c) {
    w.u8(c.col);
    w.u8(c.row);
}
function readCell(r) {
    const col = r.u8();
    const row = r.u8();
    return { col, row };
}
function writePos(w, p) {
    w.f32(p.col);
    w.f32(p.row);
}
function readPos(r) {
    const col = r.f32();
    const row = r.f32();
    return { col, row };
}
// ---------------------------------------------------------------------------
// Per-tick events (TowerAttack / UnitAttack / SplashShot / SplashImpact)
// ---------------------------------------------------------------------------
function writeTowerAttack(w, a) {
    w.string(a.towerId);
    w.string(a.targetUnitId);
    w.u8(TOWER_TYPE_TO_INDEX[a.towerType]);
    w.u8(a.laneIndex);
    writeCell(w, a.towerCell);
    writePos(w, a.targetPos);
}
function readTowerAttack(r) {
    const towerId = r.string();
    const targetUnitId = r.string();
    const towerType = TOWER_TYPE_INDEX[r.u8()];
    const laneIndex = r.u8();
    const towerCell = readCell(r);
    const targetPos = readPos(r);
    return { towerId, targetUnitId, towerType, laneIndex, towerCell, targetPos };
}
function writeUnitAttack(w, a) {
    w.string(a.unitId);
    w.u8(UNIT_TYPE_TO_INDEX[a.unitType]);
    w.string(a.towerId);
    w.u8(a.laneIndex);
    writePos(w, a.unitPos);
    writeCell(w, a.towerCell);
}
function readUnitAttack(r) {
    const unitId = r.string();
    const unitType = UNIT_TYPE_INDEX[r.u8()];
    const towerId = r.string();
    const laneIndex = r.u8();
    const unitPos = readPos(r);
    const towerCell = readCell(r);
    return { unitId, unitType, towerId, laneIndex, unitPos, towerCell };
}
function writeSplashShot(w, s) {
    w.string(s.towerId);
    w.u8(TOWER_TYPE_TO_INDEX[s.towerType]);
    w.u8(s.laneIndex);
    writeCell(w, s.originCell);
    writePos(w, s.center);
    w.f32(s.radius);
    w.u32(s.fireTick);
    w.u32(s.impactTick);
}
function readSplashShot(r) {
    const towerId = r.string();
    const towerType = TOWER_TYPE_INDEX[r.u8()];
    const laneIndex = r.u8();
    const originCell = readCell(r);
    const center = readPos(r);
    const radius = r.f32();
    const fireTick = r.u32();
    const impactTick = r.u32();
    return { towerId, towerType, laneIndex, originCell, center, radius, fireTick, impactTick };
}
function writeSplashImpact(w, s) {
    w.string(s.towerId);
    w.u8(TOWER_TYPE_TO_INDEX[s.towerType]);
    w.u8(s.laneIndex);
    writePos(w, s.center);
    w.f32(s.radius);
}
function readSplashImpact(r) {
    const towerId = r.string();
    const towerType = TOWER_TYPE_INDEX[r.u8()];
    const laneIndex = r.u8();
    const center = readPos(r);
    const radius = r.f32();
    return { towerId, towerType, laneIndex, center, radius };
}
// ---------------------------------------------------------------------------
// Full snapshots: TowerSnapshot, UnitSnapshot, PlayerSnapshot
// ---------------------------------------------------------------------------
function writeTowerSnapshot(w, t) {
    w.string(t.id);
    w.u8(TOWER_TYPE_TO_INDEX[t.type]);
    writeCell(w, t.cell);
    w.u16(t.hp);
    w.u16(t.maxHp);
    w.f32(t.buildProgress);
    w.f32(t.sellProgress);
    w.u32(t.goldSpent);
}
function readTowerSnapshot(r) {
    const id = r.string();
    const type = TOWER_TYPE_INDEX[r.u8()];
    const cell = readCell(r);
    const hp = r.u16();
    const maxHp = r.u16();
    const buildProgress = r.f32();
    const sellProgress = r.f32();
    const goldSpent = r.u32();
    return { id, type, cell, hp, maxHp, buildProgress, sellProgress, goldSpent };
}
function writeUnitSnapshot(w, u) {
    w.string(u.id);
    w.u8(UNIT_TYPE_TO_INDEX[u.type]);
    // hp/maxHp are u32: T5+ over-formula units exceed u16's 65535 cap (e.g. titan ~1.75M).
    w.u32(u.hp);
    w.u32(u.maxHp);
    w.u8(u.armor);
    w.f32(u.x);
    w.f32(u.y);
    w.f32(u.vx);
    w.f32(u.vy);
    w.string(u.ownerId);
}
function readUnitSnapshot(r) {
    const id = r.string();
    const type = UNIT_TYPE_INDEX[r.u8()];
    const hp = r.u32();
    const maxHp = r.u32();
    const armor = r.u8();
    const x = r.f32();
    const y = r.f32();
    const vx = r.f32();
    const vy = r.f32();
    const ownerId = r.string();
    return { id, type, hp, maxHp, armor, x, y, vx, vy, ownerId };
}
function writeStockpileEntry(w, type, s) {
    w.u8(UNIT_TYPE_TO_INDEX[type]);
    w.u16(s.stockpile);
    writeNullableTick(w, s.nextFillTick);
    writeNullableTick(w, s.unlockTick);
}
function readStockpileEntry(r) {
    const type = UNIT_TYPE_INDEX[r.u8()];
    const stockpile = r.u16();
    const nextFillTick = readNullableTick(r);
    const unlockTick = readNullableTick(r);
    return { type, snap: { stockpile, nextFillTick, unlockTick } };
}
const PLAYER_FLAG_IS_BOT = 1 << 0;
const PLAYER_FLAG_HAS_LEFT = 1 << 1;
// Optional FactionId encoding: 1 byte. 0 = null; 1+index = faction. Adding
// new factions appends to FACTION_IDS so old encodings stay valid.
function writeOptionalFactionId(w, id) {
    if (id === null) {
        w.u8(0);
        return;
    }
    const idx = FACTION_IDS.indexOf(id);
    if (idx < 0)
        throw new Error(`unknown faction id: ${id}`);
    w.u8(idx + 1);
}
function readOptionalFactionId(r) {
    const v = r.u8();
    if (v === 0)
        return null;
    const id = FACTION_IDS[v - 1];
    if (id === undefined)
        throw new Error(`unknown faction byte: ${v}`);
    return id;
}
function writeFactionPicks(w, fp) {
    writeOptionalFactionId(w, fp.t3);
    writeOptionalFactionId(w, fp.t4);
    writeOptionalFactionId(w, fp.t5);
}
function readFactionPicks(r) {
    return {
        t3: readOptionalFactionId(r),
        t4: readOptionalFactionId(r),
        t5: readOptionalFactionId(r),
    };
}
function writePlayerSnapshot(w, p) {
    w.string(p.playerId);
    w.bigString(p.displayName);
    w.u32(p.gold);
    w.u32(p.income);
    w.u16(p.lives);
    w.u32(p.techPoints);
    w.u8(p.laneExpansionLevel);
    w.varuint(p.techOwned.length);
    for (const t of p.techOwned)
        w.string(t);
    writeFactionPicks(w, p.factionPicks);
    let flags = 0;
    if (p.isBot === true)
        flags |= PLAYER_FLAG_IS_BOT;
    if (p.hasLeft === true)
        flags |= PLAYER_FLAG_HAS_LEFT;
    w.u8(flags);
    w.varuint(p.towers.length);
    for (const t of p.towers)
        writeTowerSnapshot(w, t);
    w.varuint(p.units.length);
    for (const u of p.units)
        writeUnitSnapshot(w, u);
    const stockpileKeys = Object.keys(p.unitStockpile);
    w.varuint(stockpileKeys.length);
    for (const k of stockpileKeys)
        writeStockpileEntry(w, k, p.unitStockpile[k]);
}
function readPlayerSnapshot(r) {
    const playerId = r.string();
    const displayName = r.bigString();
    const gold = r.u32();
    const income = r.u32();
    const lives = r.u16();
    const techPoints = r.u32();
    const laneExpansionLevel = r.u8();
    const techCount = r.varuint();
    const techOwned = new Array(techCount);
    for (let i = 0; i < techCount; i++)
        techOwned[i] = r.string();
    const factionPicks = readFactionPicks(r);
    const flags = r.u8();
    const towerCount = r.varuint();
    const towers = new Array(towerCount);
    for (let i = 0; i < towerCount; i++)
        towers[i] = readTowerSnapshot(r);
    const unitCount = r.varuint();
    const units = new Array(unitCount);
    for (let i = 0; i < unitCount; i++)
        units[i] = readUnitSnapshot(r);
    const stockpileCount = r.varuint();
    const unitStockpile = {};
    for (let i = 0; i < stockpileCount; i++) {
        const e = readStockpileEntry(r);
        unitStockpile[e.type] = e.snap;
    }
    const out = {
        playerId, displayName, gold, income, lives, techPoints, laneExpansionLevel,
        techOwned, factionPicks, towers, units, unitStockpile,
    };
    if ((flags & PLAYER_FLAG_IS_BOT) !== 0)
        out.isBot = true;
    if ((flags & PLAYER_FLAG_HAS_LEFT) !== 0)
        out.hasLeft = true;
    return out;
}
// ---------------------------------------------------------------------------
// Deltas: TowerDelta, UnitDelta, PlayerDelta
// ---------------------------------------------------------------------------
const TOWER_FLAG_TYPE = 1 << 0;
const TOWER_FLAG_CELL = 1 << 1;
const TOWER_FLAG_MAXHP = 1 << 2;
const TOWER_FLAG_GOLDSPENT = 1 << 3;
const TOWER_FLAG_HP = 1 << 4;
const TOWER_FLAG_BUILDPROGRESS = 1 << 5;
const TOWER_FLAG_SELLPROGRESS = 1 << 6;
function writeTowerDelta(w, t) {
    w.string(t.id);
    let flags = 0;
    if (t.type !== undefined)
        flags |= TOWER_FLAG_TYPE;
    if (t.cell !== undefined)
        flags |= TOWER_FLAG_CELL;
    if (t.maxHp !== undefined)
        flags |= TOWER_FLAG_MAXHP;
    if (t.goldSpent !== undefined)
        flags |= TOWER_FLAG_GOLDSPENT;
    if (t.hp !== undefined)
        flags |= TOWER_FLAG_HP;
    if (t.buildProgress !== undefined)
        flags |= TOWER_FLAG_BUILDPROGRESS;
    if (t.sellProgress !== undefined)
        flags |= TOWER_FLAG_SELLPROGRESS;
    w.u8(flags);
    if (t.type !== undefined)
        w.u8(TOWER_TYPE_TO_INDEX[t.type]);
    if (t.cell !== undefined)
        writeCell(w, t.cell);
    if (t.maxHp !== undefined)
        w.u16(t.maxHp);
    if (t.goldSpent !== undefined)
        w.u32(t.goldSpent);
    if (t.hp !== undefined)
        w.u16(t.hp);
    if (t.buildProgress !== undefined)
        w.f32(t.buildProgress);
    if (t.sellProgress !== undefined)
        w.f32(t.sellProgress);
}
function readTowerDelta(r) {
    const id = r.string();
    const flags = r.u8();
    const out = { id };
    if ((flags & TOWER_FLAG_TYPE) !== 0)
        out.type = TOWER_TYPE_INDEX[r.u8()];
    if ((flags & TOWER_FLAG_CELL) !== 0)
        out.cell = readCell(r);
    if ((flags & TOWER_FLAG_MAXHP) !== 0)
        out.maxHp = r.u16();
    if ((flags & TOWER_FLAG_GOLDSPENT) !== 0)
        out.goldSpent = r.u32();
    if ((flags & TOWER_FLAG_HP) !== 0)
        out.hp = r.u16();
    if ((flags & TOWER_FLAG_BUILDPROGRESS) !== 0)
        out.buildProgress = r.f32();
    if ((flags & TOWER_FLAG_SELLPROGRESS) !== 0)
        out.sellProgress = r.f32();
    return out;
}
const UNIT_FLAG_TYPE = 1 << 0;
const UNIT_FLAG_OWNERID = 1 << 1;
const UNIT_FLAG_ARMOR = 1 << 2;
const UNIT_FLAG_MAXHP = 1 << 3;
const UNIT_FLAG_HP = 1 << 4;
const UNIT_FLAG_X = 1 << 5;
const UNIT_FLAG_Y = 1 << 6;
const UNIT_FLAG_VX = 1 << 7;
const UNIT_FLAG_VY = 1 << 8;
function writeUnitDelta(w, u) {
    w.string(u.id);
    let flags = 0;
    if (u.type !== undefined)
        flags |= UNIT_FLAG_TYPE;
    if (u.ownerId !== undefined)
        flags |= UNIT_FLAG_OWNERID;
    if (u.armor !== undefined)
        flags |= UNIT_FLAG_ARMOR;
    if (u.maxHp !== undefined)
        flags |= UNIT_FLAG_MAXHP;
    if (u.hp !== undefined)
        flags |= UNIT_FLAG_HP;
    if (u.x !== undefined)
        flags |= UNIT_FLAG_X;
    if (u.y !== undefined)
        flags |= UNIT_FLAG_Y;
    if (u.vx !== undefined)
        flags |= UNIT_FLAG_VX;
    if (u.vy !== undefined)
        flags |= UNIT_FLAG_VY;
    w.u16(flags);
    if (u.type !== undefined)
        w.u8(UNIT_TYPE_TO_INDEX[u.type]);
    if (u.ownerId !== undefined)
        w.string(u.ownerId);
    if (u.armor !== undefined)
        w.u8(u.armor);
    if (u.maxHp !== undefined)
        w.u32(u.maxHp);
    if (u.hp !== undefined)
        w.u32(u.hp);
    if (u.x !== undefined)
        w.f32(u.x);
    if (u.y !== undefined)
        w.f32(u.y);
    if (u.vx !== undefined)
        w.f32(u.vx);
    if (u.vy !== undefined)
        w.f32(u.vy);
}
function readUnitDelta(r) {
    const id = r.string();
    const flags = r.u16();
    const out = { id };
    if ((flags & UNIT_FLAG_TYPE) !== 0)
        out.type = UNIT_TYPE_INDEX[r.u8()];
    if ((flags & UNIT_FLAG_OWNERID) !== 0)
        out.ownerId = r.string();
    if ((flags & UNIT_FLAG_ARMOR) !== 0)
        out.armor = r.u8();
    if ((flags & UNIT_FLAG_MAXHP) !== 0)
        out.maxHp = r.u32();
    if ((flags & UNIT_FLAG_HP) !== 0)
        out.hp = r.u32();
    if ((flags & UNIT_FLAG_X) !== 0)
        out.x = r.f32();
    if ((flags & UNIT_FLAG_Y) !== 0)
        out.y = r.f32();
    if ((flags & UNIT_FLAG_VX) !== 0)
        out.vx = r.f32();
    if ((flags & UNIT_FLAG_VY) !== 0)
        out.vy = r.f32();
    return out;
}
const PD_FLAG_GOLD = 1 << 0;
const PD_FLAG_INCOME = 1 << 1;
const PD_FLAG_LIVES = 1 << 2;
const PD_FLAG_TECHPOINTS = 1 << 3;
const PD_FLAG_LANEEXP = 1 << 4;
const PD_FLAG_TECHOWNED = 1 << 5;
const PD_FLAG_HASLEFT = 1 << 6;
const PD_FLAG_TOWERS_ADDED = 1 << 7;
const PD_FLAG_TOWERS_UPDATED = 1 << 8;
const PD_FLAG_TOWERS_REMOVED = 1 << 9;
const PD_FLAG_UNITS_ADDED = 1 << 10;
const PD_FLAG_UNITS_UPDATED = 1 << 11;
const PD_FLAG_UNITS_REMOVED = 1 << 12;
const PD_FLAG_STOCKPILE_CHANGED = 1 << 13;
const PD_FLAG_FACTIONS = 1 << 14;
function writePlayerDelta(w, dp) {
    w.string(dp.playerId);
    let flags = 0;
    if (dp.gold !== undefined)
        flags |= PD_FLAG_GOLD;
    if (dp.income !== undefined)
        flags |= PD_FLAG_INCOME;
    if (dp.lives !== undefined)
        flags |= PD_FLAG_LIVES;
    if (dp.techPoints !== undefined)
        flags |= PD_FLAG_TECHPOINTS;
    if (dp.laneExpansionLevel !== undefined)
        flags |= PD_FLAG_LANEEXP;
    if (dp.techOwned !== undefined)
        flags |= PD_FLAG_TECHOWNED;
    if (dp.hasLeft === true)
        flags |= PD_FLAG_HASLEFT;
    if (dp.towersAdded !== undefined)
        flags |= PD_FLAG_TOWERS_ADDED;
    if (dp.towersUpdated !== undefined)
        flags |= PD_FLAG_TOWERS_UPDATED;
    if (dp.towersRemoved !== undefined)
        flags |= PD_FLAG_TOWERS_REMOVED;
    if (dp.unitsAdded !== undefined)
        flags |= PD_FLAG_UNITS_ADDED;
    if (dp.unitsUpdated !== undefined)
        flags |= PD_FLAG_UNITS_UPDATED;
    if (dp.unitsRemoved !== undefined)
        flags |= PD_FLAG_UNITS_REMOVED;
    if (dp.stockpileChanged !== undefined)
        flags |= PD_FLAG_STOCKPILE_CHANGED;
    if (dp.factionPicks !== undefined)
        flags |= PD_FLAG_FACTIONS;
    w.u16(flags);
    if (dp.gold !== undefined)
        w.u32(dp.gold);
    if (dp.income !== undefined)
        w.u32(dp.income);
    if (dp.lives !== undefined)
        w.u16(dp.lives);
    if (dp.techPoints !== undefined)
        w.u32(dp.techPoints);
    if (dp.laneExpansionLevel !== undefined)
        w.u8(dp.laneExpansionLevel);
    if (dp.techOwned !== undefined) {
        w.varuint(dp.techOwned.length);
        for (const t of dp.techOwned)
            w.string(t);
    }
    // hasLeft handled via flag
    if (dp.towersAdded !== undefined) {
        w.varuint(dp.towersAdded.length);
        for (const t of dp.towersAdded)
            writeTowerDelta(w, t);
    }
    if (dp.towersUpdated !== undefined) {
        w.varuint(dp.towersUpdated.length);
        for (const t of dp.towersUpdated)
            writeTowerDelta(w, t);
    }
    if (dp.towersRemoved !== undefined) {
        w.varuint(dp.towersRemoved.length);
        for (const id of dp.towersRemoved)
            w.string(id);
    }
    if (dp.unitsAdded !== undefined) {
        w.varuint(dp.unitsAdded.length);
        for (const u of dp.unitsAdded)
            writeUnitDelta(w, u);
    }
    if (dp.unitsUpdated !== undefined) {
        w.varuint(dp.unitsUpdated.length);
        for (const u of dp.unitsUpdated)
            writeUnitDelta(w, u);
    }
    if (dp.unitsRemoved !== undefined) {
        w.varuint(dp.unitsRemoved.length);
        for (const id of dp.unitsRemoved)
            w.string(id);
    }
    if (dp.stockpileChanged !== undefined) {
        const keys = Object.keys(dp.stockpileChanged);
        w.varuint(keys.length);
        for (const k of keys)
            writeStockpileEntry(w, k, dp.stockpileChanged[k]);
    }
    if (dp.factionPicks !== undefined) {
        writeFactionPicks(w, dp.factionPicks);
    }
}
function readPlayerDelta(r) {
    const playerId = r.string();
    const flags = r.u16();
    const out = { playerId };
    if ((flags & PD_FLAG_GOLD) !== 0)
        out.gold = r.u32();
    if ((flags & PD_FLAG_INCOME) !== 0)
        out.income = r.u32();
    if ((flags & PD_FLAG_LIVES) !== 0)
        out.lives = r.u16();
    if ((flags & PD_FLAG_TECHPOINTS) !== 0)
        out.techPoints = r.u32();
    if ((flags & PD_FLAG_LANEEXP) !== 0)
        out.laneExpansionLevel = r.u8();
    if ((flags & PD_FLAG_TECHOWNED) !== 0) {
        const n = r.varuint();
        const arr = new Array(n);
        for (let i = 0; i < n; i++)
            arr[i] = r.string();
        out.techOwned = arr;
    }
    if ((flags & PD_FLAG_HASLEFT) !== 0)
        out.hasLeft = true;
    if ((flags & PD_FLAG_TOWERS_ADDED) !== 0) {
        const n = r.varuint();
        const arr = new Array(n);
        for (let i = 0; i < n; i++)
            arr[i] = readTowerDelta(r);
        out.towersAdded = arr;
    }
    if ((flags & PD_FLAG_TOWERS_UPDATED) !== 0) {
        const n = r.varuint();
        const arr = new Array(n);
        for (let i = 0; i < n; i++)
            arr[i] = readTowerDelta(r);
        out.towersUpdated = arr;
    }
    if ((flags & PD_FLAG_TOWERS_REMOVED) !== 0) {
        const n = r.varuint();
        const arr = new Array(n);
        for (let i = 0; i < n; i++)
            arr[i] = r.string();
        out.towersRemoved = arr;
    }
    if ((flags & PD_FLAG_UNITS_ADDED) !== 0) {
        const n = r.varuint();
        const arr = new Array(n);
        for (let i = 0; i < n; i++)
            arr[i] = readUnitDelta(r);
        out.unitsAdded = arr;
    }
    if ((flags & PD_FLAG_UNITS_UPDATED) !== 0) {
        const n = r.varuint();
        const arr = new Array(n);
        for (let i = 0; i < n; i++)
            arr[i] = readUnitDelta(r);
        out.unitsUpdated = arr;
    }
    if ((flags & PD_FLAG_UNITS_REMOVED) !== 0) {
        const n = r.varuint();
        const arr = new Array(n);
        for (let i = 0; i < n; i++)
            arr[i] = r.string();
        out.unitsRemoved = arr;
    }
    if ((flags & PD_FLAG_STOCKPILE_CHANGED) !== 0) {
        const n = r.varuint();
        const sp = {};
        for (let i = 0; i < n; i++) {
            const e = readStockpileEntry(r);
            sp[e.type] = e.snap;
        }
        out.stockpileChanged = sp;
    }
    if ((flags & PD_FLAG_FACTIONS) !== 0) {
        out.factionPicks = readFactionPicks(r);
    }
    return out;
}
// ---------------------------------------------------------------------------
// Top-level encode / decode
// ---------------------------------------------------------------------------
function writePerTickEvents(w, attacks, unitAttacks, splashShots, splashImpacts) {
    w.varuint(attacks.length);
    for (const a of attacks)
        writeTowerAttack(w, a);
    w.varuint(unitAttacks.length);
    for (const a of unitAttacks)
        writeUnitAttack(w, a);
    w.varuint(splashShots.length);
    for (const s of splashShots)
        writeSplashShot(w, s);
    w.varuint(splashImpacts.length);
    for (const s of splashImpacts)
        writeSplashImpact(w, s);
}
function readPerTickEvents(r) {
    const aN = r.varuint();
    const attacks = new Array(aN);
    for (let i = 0; i < aN; i++)
        attacks[i] = readTowerAttack(r);
    const uaN = r.varuint();
    const unitAttacks = new Array(uaN);
    for (let i = 0; i < uaN; i++)
        unitAttacks[i] = readUnitAttack(r);
    const ssN = r.varuint();
    const splashShots = new Array(ssN);
    for (let i = 0; i < ssN; i++)
        splashShots[i] = readSplashShot(r);
    const siN = r.varuint();
    const splashImpacts = new Array(siN);
    for (let i = 0; i < siN; i++)
        splashImpacts[i] = readSplashImpact(r);
    return { attacks, unitAttacks, splashShots, splashImpacts };
}
export function encodeSnapshot(msg) {
    const w = new Writer(2048);
    w.u8(MAGIC);
    w.u8(VERSION);
    if (msg.type === "snapshot") {
        w.u8(MSG_SNAPSHOT);
        w.u32(msg.tick);
        writePerTickEvents(w, msg.attacks, msg.unitAttacks, msg.splashShots, msg.splashImpacts);
        w.varuint(msg.players.length);
        for (const p of msg.players)
            writePlayerSnapshot(w, p);
    }
    else {
        w.u8(MSG_SNAPSHOT_DELTA);
        w.u32(msg.tick);
        w.u32(msg.baseTick);
        writePerTickEvents(w, msg.attacks, msg.unitAttacks, msg.splashShots, msg.splashImpacts);
        w.varuint(msg.players.length);
        for (const p of msg.players)
            writePlayerDelta(w, p);
    }
    return w.finish();
}
export function decodeSnapshot(buf) {
    const u8 = buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf;
    const r = new Reader(u8);
    const magic = r.u8();
    if (magic !== MAGIC)
        throw new Error(`bad magic byte: 0x${magic.toString(16)}`);
    const version = r.u8();
    if (version !== VERSION)
        throw new Error(`unsupported codec version: ${version} (expected ${VERSION})`);
    const messageType = r.u8();
    if (messageType === MSG_SNAPSHOT) {
        const tick = r.u32();
        const events = readPerTickEvents(r);
        const playerCount = r.varuint();
        const players = new Array(playerCount);
        for (let i = 0; i < playerCount; i++)
            players[i] = readPlayerSnapshot(r);
        return {
            type: "snapshot", tick,
            attacks: events.attacks, unitAttacks: events.unitAttacks,
            splashShots: events.splashShots, splashImpacts: events.splashImpacts,
            players,
        };
    }
    else if (messageType === MSG_SNAPSHOT_DELTA) {
        const tick = r.u32();
        const baseTick = r.u32();
        const events = readPerTickEvents(r);
        const playerCount = r.varuint();
        const players = new Array(playerCount);
        for (let i = 0; i < playerCount; i++)
            players[i] = readPlayerDelta(r);
        return {
            type: "snapshot_delta", tick, baseTick,
            attacks: events.attacks, unitAttacks: events.unitAttacks,
            splashShots: events.splashShots, splashImpacts: events.splashImpacts,
            players,
        };
    }
    else {
        throw new Error(`unknown messageType: ${messageType}`);
    }
}
